import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import EventShareControl from '../components/EventShareControl';
import AddToCalendarButton from '../components/AddToCalendarButton';
import InstallAppMenuItem from '../components/InstallAppMenuItem';
import { rememberDeepLink } from '../utils/deepLink';
import {
  ArrowLeft, MapPin, Clock, Tag, Euro, Phone, MessageCircle,
  Globe, Building2, ChevronRight, CalendarX2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Remember this page so an install-from-here returns the user to it.
    rememberDeepLink(`/events/${id}`);
    axios.get(`${API}/api/events/${id}`)
      .then((res) => setEvent(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    axios.post(`${API}/api/posts/${id}/click`, {}).catch(() => {});
  }, [id]);

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://find-2-day.com'}/api/share/events/${id}`;
  const biz = event?.business || {};

  const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const fmtTime = (d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="event-detail-loading">
        <div className="animate-pulse text-gray-400">{t('loading', 'Loading…')}</div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center" data-testid="event-detail-notfound">
        <CalendarX2 className="h-12 w-12 text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800">{t('eventDetail.notFoundTitle', 'Event not available')}</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-xs">{t('eventDetail.notFoundBody', 'This event may have ended or been removed.')}</p>
        <button onClick={() => navigate('/events')} className="mt-5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700" data-testid="event-detail-browse-btn">
          {t('eventDetail.browseEvents', 'Browse events')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="event-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm" data-testid="event-detail-back">
            <ArrowLeft className="h-4 w-4" /> {t('back', 'Back')}
          </button>
          <EventShareControl
            url={shareUrl}
            title={`${event.title} – ${biz.businessName || ''}`.trim()}
            text={event.description || t('shareDefaultText', 'Check out this on Find-2-Day')}
            testId={`event-detail-${event.id}`}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4">
        {/* Cover image */}
        {event.image && (
          <div className="mt-4 rounded-2xl overflow-hidden bg-gray-100">
            <img src={event.image} alt={event.title} className="w-full max-h-80 object-cover" data-testid="event-detail-image" />
          </div>
        )}

        {/* Title + meta */}
        <div className="mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            {event.category && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium" data-testid="event-detail-category">
                <Tag className="h-3 w-3" /> {event.category}
              </span>
            )}
            {event.boosted && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold" data-testid="event-detail-sponsored">
                {t('sponsored', 'Sponsored')}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2" data-testid="event-detail-title">{event.title}</h1>
        </div>

        {/* Key facts */}
        <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
          <div className="flex items-start gap-3 p-4" data-testid="event-detail-datetime">
            <Clock className="h-5 w-5 text-purple-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-gray-900">{fmtDate(event.startDate)}</p>
              <p className="text-gray-500">
                {fmtTime(event.startDate)}
                {event.endDate ? ` – ${new Date(event.endDate).toDateString() === new Date(event.startDate).toDateString() ? fmtTime(event.endDate) : `${fmtDate(event.endDate)} ${fmtTime(event.endDate)}`}` : ''}
              </p>
            </div>
          </div>
          {event.location && (
            <div className="flex items-center gap-3 p-4 text-sm" data-testid="event-detail-location">
              <MapPin className="h-5 w-5 text-purple-500 shrink-0" />
              <span className="text-gray-800">{event.location}</span>
            </div>
          )}
          <div className="flex items-center gap-3 p-4 text-sm" data-testid="event-detail-price">
            <Euro className="h-5 w-5 text-purple-500 shrink-0" />
            <span className="text-gray-800">{event.price != null && event.price > 0 ? `€${Number(event.price).toFixed(2)}` : t('eventDetail.free', 'Free entry')}</span>
          </div>
        </div>

        {/* Add to calendar */}
        <div className="mt-3 relative z-10">
          <AddToCalendarButton event={event} business={biz} />
        </div>

        {/* Description */}
        {event.description && (
          <div className="mt-5" data-testid="event-detail-description">
            <h2 className="text-base font-semibold text-gray-900 mb-1">{t('eventDetail.about', 'About this event')}</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{event.description}</p>
          </div>
        )}

        {/* Business info */}
        {biz.id && (
          <div className="mt-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('eventDetail.hostedBy', 'Hosted by')}</h2>
            <button
              onClick={() => navigate(`/business/${biz.id}`)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-purple-200 text-left"
              data-testid="event-detail-business"
            >
              {biz.logoUrl ? (
                <img src={biz.logoUrl} alt={biz.businessName} className="h-12 w-12 rounded-lg object-cover border border-gray-100" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center"><Building2 className="h-6 w-6 text-purple-500" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{biz.businessName}</p>
                <p className="text-xs text-gray-500 truncate">{[biz.category, biz.city].filter(Boolean).join(' · ')}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-300" />
            </button>

            {/* Contact shortcuts */}
            <div className="flex flex-wrap gap-2 mt-2">
              {biz.phone && (
                <a href={`tel:${biz.phone}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50" data-testid="event-detail-phone">
                  <Phone className="h-4 w-4" /> {t('call', 'Call')}
                </a>
              )}
              {biz.whatsapp && (
                <a href={`https://wa.me/${biz.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50" data-testid="event-detail-whatsapp">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              )}
              {biz.website && (
                <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50" data-testid="event-detail-website">
                  <Globe className="h-4 w-4" /> {t('website', 'Website')}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Install app prompt (hidden if already installed) */}
        <div className="mt-6">
          <InstallAppMenuItem surface="event_detail" variant="card" testId="event-detail-install" />
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default EventDetail;
